/** Stable-enough suffix for demo emails and links (avoids obvious duplicates per click). */
export function demoSuffix() {
  return `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export const SAMPLE_DEMO_PASSWORD = 'DemoPass123!';

export function sampleRegistrationCredentials() {
  return {
    email: `demo.${demoSuffix()}@example.com`,
    password: SAMPLE_DEMO_PASSWORD,
  };
}

/** Quick-fill presets for the login page — one per role tier. */
export const LOGIN_PRESETS = [
  {
    role:     'SuperAdmin',
    labelKey: 'loginPresetSuperAdmin',
    email:    'superadmin@coretask.io',
    password: 'SuperAdmin1!',
    color:    'brand',   // used for styling
  },
  {
    role:     'Admin',
    labelKey: 'loginPresetAdmin',
    email:    'ahmed@coretask.dev',
    password: 'Admin1234!',
    color:    'emerald',
  },
  {
    role:     'Member',
    labelKey: 'loginPresetMember',
    email:    'member@coretask.dev',
    password: 'Member1234!',
    color:    'slate',
  },
];

/** Values for New Project modal (names match local state keys). */
export function sampleNewProjectModalState(memberIdsPrefer = []) {
  return sampleProjectPresets(memberIdsPrefer)[0];
}

function isoDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function picks(memberIds, n) {
  const ids = Array.isArray(memberIds) && memberIds.length > 0 ? memberIds : ['u1'];
  return ids.slice(0, n);
}

/** Three real project presets for the New Project modal. */
export function sampleProjectPresets(memberIdsPrefer = []) {
  return [
    {
      label: 'E-Commerce Redesign',
      name: 'E-Commerce Redesign',
      description:
        'Full redesign of the storefront, checkout flow, and mobile experience. Goals: increase conversion rate by 20%, reduce cart abandonment, and launch a refreshed brand identity across all customer touchpoints.',
      color: '#6366f1',
      startDate: isoDate(0),
      endDate: isoDate(45),
      selectedMembers: picks(memberIdsPrefer, 4),
    },
    {
      label: 'Mobile App — iOS & Android',
      name: 'Mobile App — iOS & Android',
      description:
        'Build and ship the v1 native mobile app. Covers authentication, push notifications, offline mode, and App Store / Play Store submission. Target: public beta in 8 weeks.',
      color: '#0ea5e9',
      startDate: isoDate(0),
      endDate: isoDate(60),
      selectedMembers: picks(memberIdsPrefer, 3),
    },
    {
      label: 'Q3 Marketing Campaign',
      name: 'Q3 Marketing Campaign',
      description:
        'Plan and execute the Q3 growth campaign: social media, email sequences, landing pages, and paid ads. KPIs: 15 % lift in sign-ups, 10 k new newsletter subscribers, and 3 press mentions by quarter end.',
      color: '#16a34a',
      startDate: isoDate(0),
      endDate: isoDate(30),
      selectedMembers: picks(memberIdsPrefer, 2),
    },
  ];
}

const SAMPLE_TASK_TITLES = [
  'Define acceptance criteria',
  'Wire API contract',
  'QA smoke on staging',
  'Draft release notes',
  'Sync with design on spacing',
];

/** Rotates deterministic titles per column/table row index. */
export function pickSampleTaskTitle(index = 0) {
  const n = Number.isFinite(index) ? index : 0;
  return SAMPLE_TASK_TITLES[Math.abs(n) % SAMPLE_TASK_TITLES.length];
}

export function sampleInviteForm(allProjectRows = []) {
  return sampleInvitePresets(allProjectRows)[0];
}

/** Multiple realistic invite presets for the Invite Member modal. */
export function sampleInvitePresets(allProjectRows = []) {
  const ids = (allProjectRows || []).map((p) => Number(p.id)).filter((id) => Number.isFinite(id));
  const firstId  = ids.slice(0, 1);
  const firstTwo = ids.slice(0, 2);
  const all      = ids;

  return [
    {
      label: 'Sara Mitchell — Designer',
      name: 'Sara Mitchell',
      email: `sara.mitchell.${demoSuffix().slice(-4)}@studio.io`,
      role: 'Designer',
      projectIds: firstTwo,
    },
    {
      label: 'James Carter — Developer',
      name: 'James Carter',
      email: `james.carter.${demoSuffix().slice(-4)}@devteam.co`,
      role: 'Developer',
      projectIds: all,
    },
    {
      label: 'Lena Hoffmann — Manager',
      name: 'Lena Hoffmann',
      email: `lena.hoffmann.${demoSuffix().slice(-4)}@corp.eu`,
      role: 'Manager',
      projectIds: all,
    },
    {
      label: 'Omar Khalid — Viewer',
      name: 'Omar Khalid',
      email: `omar.khalid.${demoSuffix().slice(-4)}@client.ae`,
      role: 'Viewer',
      projectIds: firstId,
    },
    {
      label: 'Priya Nair — Developer',
      name: 'Priya Nair',
      email: `priya.nair.${demoSuffix().slice(-4)}@tech.in`,
      role: 'Developer',
      projectIds: firstTwo,
    },
  ];
}

export function sampleMeetingLink() {
  return `https://meet.example.com/demo-room-${demoSuffix().slice(-8)}`;
}

export function sampleGroupForm(workspaceMemberRows = []) {
  const chosen = workspaceMemberRows.slice(0, 2);
  const initials = chosen
    .flatMap((m) => m.name.split(/\s+/))
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return {
    name: `Design sync (${demoSuffix().slice(-4)})`,
    avatar: initials || 'GR',
    members: chosen.map((m) => m.name),
  };
}

export const SAMPLE_CATEGORY_DRAFT = Object.freeze({
  name: 'Engineering',
  color: '#0ea5e9',
  icon: '⚙️',
});

export function sampleLabelDraft() {
  return {
    name: `priority-${demoSuffix().slice(-4)}`,
    color: '#dc2626',
  };
}

export function sampleSubtaskTitle(taskTitle = '') {
  const tail = demoSuffix().slice(-4);
  const base = taskTitle.trim();
  if (!base) return `Clarify scope (${tail})`;
  return `Follow-up · ${base.slice(0, 48)} (${tail})`;
}

export function sampleProfileFields() {
  return {
    name: 'Taylor Morgan',
    bio: 'Demo copy for the profile panel—feel free to save or replace.',
    timezone: 'UTC',
  };
}

export function sampleWorkspaceFields() {
  return {
    name: `Demo workspace (${demoSuffix().slice(-6)})`,
    defaultProjectPermission: 'Member',
    invitation: 'Guests require admin approval.',
  };
}
